import { ref, computed, watch, provide, onMounted, nextTick, type Ref, onUnmounted } from 'vue';
import { useBaseMenuStore } from '@/common/stores/base-menu';
import { storeToRefs } from 'pinia';
import {
  type BreadCrumbInstanceInfo,
  useInstanceStore,
  type InstanceType,
} from '@/common/stores/instance';
import { useViewModeStore } from '@/common/stores/view-mode';
import { useDashboardViewStore } from '@/dashboard/stores/dashboard-view';
import { useInternational } from '@/common/locale';
import {
  DATABASE_CLUSTER_VIEW_TYPES,
  DATABASE_MULTI_VIEW_TYPES,
  DATABASE_SINGLE_VIEW_TYPES,
} from '@/database/utils/define';
import { VIEW_MODE } from '@/common/define/common.define';
import { useRoute, useRouter } from 'vue-router';
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import type { MenuInfo } from '@/common/components/organisms/baseMenu/types';
import { IconType } from '@/types/icon.types';
import { useDatabaseOverviewStore } from '@/database/stores/database-overview';
import { isEqual } from 'lodash-es';
import { OVERVIEW_TYPE } from '@/database/utils/overview.define';
import { hasDbSelect } from '@/database/utils/overview.utils';
import { invalidatePlatformDashboardMenuPath } from '@/common/lib/queryCache';
import {
  BREAD_CRUMBS_MODAL_CLASS,
  BREAD_CRUMBS_CHANGE_FAVORITE_KEY,
  DASHBOARD_MENU_TYPE,
} from './breadCrumbs.define';
import type {
  MenuMouseEvent,
  BreadCrumbMenuInfo,
  MenuPosition,
  BreadCrumbMode,
} from './breadCrumbs.types';
import { useDatabaseMetaStore } from '@/database/stores/database-meta';
import { useMessage } from '@/common/inject/useMessage';
import { hasDatabaseDashboardPermission } from '@/database/utils/permission.utils';
import { useDashboardListStore } from '@/dashboard/stores/dashboard-list';

export interface Props {
  mode?: BreadCrumbMode;
  dbInstanceType?: InstanceType;
  dbType?: string;
  dashboardIcon?: IconType | null;
}

interface InstanceButton {
  text: string;
  instanceType: InstanceType;
  isCluster?: boolean;
}

const getCharCategory = (char: string): number => {
  // SPEC - 알파벳→ 가나다 → 숫자 → 특수문자
  if (/[a-zA-Z]/.test(char)) return 0;
  if (/[\uAC00-\uD7A3\u3131-\u3163]/.test(char)) return 1;
  if (/[0-9]/.test(char)) return 2;
  return 3;
};

const compareBreadCrumbName = (a: string, b: string): number => {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const catA = getCharCategory(a[i]);
    const catB = getCharCategory(b[i]);
    if (catA !== catB) return catA - catB;
    const cmp = a[i].localeCompare(b[i]);
    if (cmp !== 0) return cmp;
  }
  return a.length - b.length;
};

const sortByName = <T extends { name?: string; id?: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => compareBreadCrumbName(a.name ?? '', b.name ?? ''));

// Common
const useMenuRoutes = () => {
  const baseMenuStore = useBaseMenuStore();
  const { menuVisitRoute } = storeToRefs(baseMenuStore);

  // GNB 부터(LNB 그룹명 제외)
  const filteredPaths = computed<MenuInfo[]>(() => {
    const visit = menuVisitRoute.value;
    const paths = visit.filter(
      (menu, i) => i === 0 || !menu.children || menu.subMenuList || i === visit.length - 1,
    );
    // sub-header를 노출하는 그룹(showSubHeader)은 leaf(LNB 항목)를 sub-header에서 다루므로
    // breadcrumb에서는 제외하고 그룹까지만 표시한다. 성능분석처럼 sub-header가 없는 그룹은 leaf까지 표시한다.
    if (
      visit[0]?.value === BASE_MENU_VALUE.KUBERNETES &&
      visit[1]?.showSubHeader &&
      paths.length > 1
    ) {
      return paths.slice(0, -1);
    }
    return paths;
  });

  const getSiblingMenuPath = (i?: number): BreadCrumbMenuInfo => {
    return filteredPaths.value[(i ?? 1) - 1];
  };

  return {
    filteredPaths,
    getSiblingMenuPath,
  };
};

// Instances
const useMenuInstances = () => {
  const { t } = useInternational();
  const { showErrorMsg } = useMessage();

  const router = useRouter();

  const databaseOverviewStore = useDatabaseOverviewStore();
  const { currentViewInfo } = storeToRefs(databaseOverviewStore);

  const instanceStore = useInstanceStore();
  const { findMultiView, setCurrentInstance } = instanceStore;
  const { instances, currentInstance } = storeToRefs(instanceStore);

  const viewModeStore = useViewModeStore();
  const { viewMode } = storeToRefs(viewModeStore);

  const instanceTitleByViewMode = computed(() => {
    return viewMode.value === VIEW_MODE.MAXGAUGE ? t('WORD.OVERVIEW') : t('WORD.DATABASE');
  });

  const viewDisplayName = computed(() => {
    const { view } = currentViewInfo.value;

    if (view === OVERVIEW_TYPE.MULTI_VIEW) {
      return t('WORD.MULTI_VIEW');
    }
    if (view === OVERVIEW_TYPE.CLUSTER_VIEW) {
      return t('WORD.CLUSTER_VIEW');
    }

    return t('WORD.SINGLE_VIEW');
  });
  const hasDbDashboardPermission = (dbType?: string) => hasDatabaseDashboardPermission(dbType);
  const instanceTypeButtons = computed<InstanceButton[]>(() => {
    if (currentViewInfo.value.view === OVERVIEW_TYPE.MULTI_VIEW) {
      return [
        { text: currentInstance.value.multiView.name, instanceType: OVERVIEW_TYPE.MULTI_VIEW },
      ];
    }
    if (currentViewInfo.value.view === OVERVIEW_TYPE.CLUSTER_VIEW) {
      return [
        { text: currentInstance.value.multiView.name, instanceType: OVERVIEW_TYPE.MULTI_VIEW },
        {
          text: currentInstance.value.singleView.name,
          instanceType: OVERVIEW_TYPE.CLUSTER_VIEW,
          isCluster: true,
        },
      ];
    }
    return [
      { text: currentInstance.value.multiView.name, instanceType: OVERVIEW_TYPE.MULTI_VIEW },
      { text: currentInstance.value.singleView.name, instanceType: OVERVIEW_TYPE.SINGLE_VIEW },
    ];
  });

  const isInstanceValidForDbType = (dbType: string, view: string | null | undefined) => {
    if (view === OVERVIEW_TYPE.MULTI_VIEW) {
      const group = instances.value?.find((i) => i.id === currentInstance.value.multiView.id);
      return group?.type === dbType;
    }
    const singleViewId = currentInstance.value.singleView.id;
    return (
      instances.value?.some(
        (group) => group.type === dbType && group.children?.some((c) => c.id === singleViewId),
      ) ?? false
    );
  };

  const resolveInstanceFromQueryId = (
    queryId: string,
    dbType: string,
    view: string | null | undefined,
    fallbackButton: InstanceButton,
  ): InstanceButton | undefined => {
    for (const group of instances.value ?? []) {
      if (group.type !== dbType) continue;
      if (view === OVERVIEW_TYPE.MULTI_VIEW && group.id === queryId) {
        return { text: group.name, instanceType: OVERVIEW_TYPE.MULTI_VIEW };
      }
      const child = group.children?.find((c) => c.id === queryId);
      if (child) {
        return {
          text: child.name,
          instanceType: fallbackButton.instanceType,
          isCluster: fallbackButton.isCluster,
        };
      }
    }
    return undefined;
  };

  const lastInstanceButton = computed<InstanceButton | undefined>(() => {
    const buttons = instanceTypeButtons.value;
    if (buttons.length === 0) return undefined;

    const button = buttons[buttons.length - 1];
    const { dbType, queryId, view } = currentViewInfo.value;
    if (!dbType) return button;

    if (isInstanceValidForDbType(dbType, view)) return button;

    if (queryId) {
      const resolved = resolveInstanceFromQueryId(queryId, dbType, view, button);
      if (resolved) return resolved;
    }

    const fallbackGroup = instances.value?.find((i) => i.type === dbType);
    if (fallbackGroup) {
      if (view === OVERVIEW_TYPE.MULTI_VIEW) {
        return { text: fallbackGroup.name, instanceType: OVERVIEW_TYPE.MULTI_VIEW };
      }
      const firstChild = sortByName(fallbackGroup.children ?? [])[0];
      if (firstChild) {
        return {
          text: firstChild.name,
          instanceType: button.instanceType,
          isCluster: button.isCluster,
        };
      }
    }

    return button;
  });

  // 멀티뷰
  const getInstanceMultiMenuList = (): BreadCrumbMenuInfo => {
    return {
      children: instances.value
        ?.filter(
          (instance) =>
            DATABASE_MULTI_VIEW_TYPES[instance.type!] || // LINK - BreadCrumbs
            instance.children?.find((child) => DATABASE_SINGLE_VIEW_TYPES[child.type!]),
        )
        .map((instance) => ({
          text: instance.name ?? 'Not Found',
          value: instance.id ?? 'Not Found',
          dbType: instance.type,
          instanceType: OVERVIEW_TYPE.MULTI_VIEW,
          children:
            instance.children
              ?.filter((child) => DATABASE_SINGLE_VIEW_TYPES[child.type!])
              .filter((child) => hasDbDashboardPermission(child.type))
              .map((child) => ({
                text: child.name ?? 'Not Found',
                value: child.id ?? 'Not Found',
                dbType: child.type,
                instanceType: OVERVIEW_TYPE.SINGLE_VIEW,
              })) ?? [],
        })),
    };
  };

  const getInstanceClusterView = (): BreadCrumbMenuInfo => {
    const clusterViewDbTypes = Object.keys(DATABASE_CLUSTER_VIEW_TYPES);
    const instanceList = useDatabaseMetaStore().getInstanceGroups();
    const clusterInstanceList = instanceList.filter((instance) =>
      instance.instances?.some((item) => clusterViewDbTypes.includes(item.type!) && item.enabled),
    );

    return {
      children: [
        ...clusterInstanceList.map((instance) => ({
          text: instance.name ?? 'Not Found',
          value: instance.folderId ?? 'Not Found',
          dbType: instance.instances?.[0]?.type,
          instanceType: OVERVIEW_TYPE.CLUSTER_VIEW,
          children: instance.instances
            ?.filter((a) => a.instanceId === a.singlestoreClusterId && a.enabled)
            .map((a) => ({
              text: a.alias || a.name || 'Not Found',
              value: a.instanceId ?? 'Not Found',
              dbType: a.type,
              instanceType: OVERVIEW_TYPE.CLUSTER_VIEW,
            })),
        })),
      ],
    };
  };

  const getInstanceMultiViewClusterClusterView = (): BreadCrumbMenuInfo => {
    const multiViewMenuList = getInstanceMultiMenuList();
    const clusterViewMenuList = getInstanceClusterView();

    return {
      children: [...(multiViewMenuList.children || []), ...(clusterViewMenuList.children || [])],
    };
  };

  const getInstanceMenuListByDbType = (): BreadCrumbMenuInfo => {
    const { dbType } = currentViewInfo.value;
    if (!dbType) return getInstanceMultiViewClusterClusterView();

    const filteredInstances = instances.value?.filter((instance) => instance.type === dbType) ?? [];
    const sortedInstances = sortByName(filteredInstances);
    const hasSingleView = DATABASE_SINGLE_VIEW_TYPES[dbType];
    if (!hasSingleView) {
      return {
        children: sortedInstances.map((instance) => ({
          text: instance.name ?? 'Not Found',
          value: instance.id ?? 'Not Found',
          dbType: instance.type,
          instanceType: OVERVIEW_TYPE.MULTI_VIEW,
          children: [],
        })),
      };
    }

    return {
      children: sortedInstances.map((instance) => ({
        text: instance.name ?? 'Not Found',
        value: instance.id ?? 'Not Found',
        dbType: instance.type,
        instanceType: OVERVIEW_TYPE.MULTI_VIEW,
        children: sortByName(instance.children ?? [])
          .filter((child) => hasDbDashboardPermission(child.type))
          .map((child) => ({
            text: child.name ?? 'Not Found',
            value: child.id ?? 'Not Found',
            dbType: child.type,
            instanceType: OVERVIEW_TYPE.SINGLE_VIEW,
          })),
      })),
    };
  };

  const getClusterViewMenuListByInstanceId = (): BreadCrumbMenuInfo => {
    const clusterViewMenuList = getInstanceClusterView().children;
    const foundClusterView = clusterViewMenuList?.find(
      (item) => item.value === currentInstance.value.multiView.id,
    );
    return { children: foundClusterView?.children || [] };
  };

  // 싱글뷰
  const getInstanceSingleMenuList = (): BreadCrumbMenuInfo => {
    const { dbType } = currentViewInfo.value;
    if (!dbType) return { children: [] };

    const filteredInstances = instances.value?.filter(
      (instance) => DATABASE_SINGLE_VIEW_TYPES[instance.type!] && instance.type === dbType,
    );

    return {
      children: sortByName(filteredInstances ?? [])
        .flatMap((instance) => instance.children ?? [])
        .filter((child) => hasDbDashboardPermission(child.type))
        .map((child) => ({
          text: child.name ?? 'Not Found',
          value: child.id ?? 'Not Found',
          dbType: child.type,
          instanceType: OVERVIEW_TYPE.SINGLE_VIEW,
        })),
    };
  };

  const setInstanceInfo = async () => {
    const { view, dbType, queryId } = currentViewInfo.value;
    try {
      if (!instances.value || !queryId) {
        throw Error('Instances Data is not defined');
      }

      if (view === OVERVIEW_TYPE.SINGLE_VIEW || view === OVERVIEW_TYPE.CLUSTER_VIEW) {
        const foundMultiView = findMultiView(queryId);
        const singleViewInfo = databaseOverviewStore.getLastSelectedOverviewInfo<
          typeof OVERVIEW_TYPE.SINGLE_VIEW
        >({
          dbType,
          viewType: view,
        });

        setCurrentInstance({
          multiView: {
            name: foundMultiView?.name ?? 'Not Found',
            id: foundMultiView?.id ?? 'Not Found',
          },
          singleView: {
            name: singleViewInfo?.alias || singleViewInfo?.name || 'Not Found',
            id: singleViewInfo?.instanceId ?? 'Not Found',
          },
          currentDbInstanceType: view,
        });

        if (hasDbSelect(currentViewInfo.value)) {
          await databaseOverviewStore.fetchDbIds(queryId);
        }
      } else if (view === OVERVIEW_TYPE.MULTI_VIEW) {
        const multiViewInfo = databaseOverviewStore.getLastSelectedOverviewInfo<
          typeof OVERVIEW_TYPE.MULTI_VIEW
        >({
          dbType,
          viewType: OVERVIEW_TYPE.MULTI_VIEW,
        });

        setCurrentInstance({
          multiView: {
            name: multiViewInfo?.name ?? 'Not Found',
            id: multiViewInfo?.folderId ?? 'Not Found',
          },
          currentDbInstanceType: OVERVIEW_TYPE.MULTI_VIEW,
        });
      }
    } catch (error) {
      console.log(error);
      showErrorMsg({ msgStr: t('NOTI.PRODUCT.INVALID_ACCESS') });
      await router.push({
        path: '/database/instance',
      });
    }
  };

  watch(
    [currentViewInfo, instances],
    async ([currViewInfo, currInstances], prev) => {
      if (
        currViewInfo.view &&
        currViewInfo.queryId &&
        !isEqual(currViewInfo, prev?.[0]) &&
        currInstances.length > 0
      ) {
        await setInstanceInfo();
      }
    },
    { immediate: true },
  );

  const overviewTypes = Object.values(OVERVIEW_TYPE) as string[];

  const appendInstanceId = (item: BreadCrumbMenuInfo): BreadCrumbMenuInfo => {
    if (!item.path) return item;
    const pathParts = item.path.split('/').filter(Boolean);
    const viewType = pathParts[pathParts.length - 1];
    if (!overviewTypes.includes(viewType)) return item;

    const targetDbType = pathParts.length >= 2 ? pathParts[0] : currentViewInfo.value.dbType;

    let group: BreadCrumbInstanceInfo | undefined;
    if (viewType === OVERVIEW_TYPE.MULTI_VIEW) {
      const currentSingleViewId = currentInstance.value.singleView.id;
      group =
        instances.value?.find(
          (g) => g.type === targetDbType && g.children?.some((c) => c.id === currentSingleViewId),
        ) ?? instances.value?.find((g) => g.type === targetDbType);
    } else {
      group = instances.value?.find((g) => g.type === targetDbType);
    }
    if (!group) return item;

    const id =
      viewType === OVERVIEW_TYPE.MULTI_VIEW ? group.id : sortByName(group.children ?? [])[0]?.id;
    if (!id) return item;
    return { ...item, path: `${item.path}?id=${id}` };
  };

  const addInstanceIdToMenuPaths = (menu: BreadCrumbMenuInfo): BreadCrumbMenuInfo => {
    return {
      ...menu,
      children: menu.children?.map((child) => {
        const processed = appendInstanceId(child);
        return child.children?.length
          ? { ...processed, children: child.children.map(appendInstanceId) }
          : processed;
      }) as BreadCrumbMenuInfo['children'],
      subMenuList: menu.subMenuList?.map((sub) => ({
        ...sub,
        children: sub.children?.map(appendInstanceId),
      })) as BreadCrumbMenuInfo['subMenuList'],
    };
  };

  return {
    instanceTitleByViewMode,
    viewDisplayName,
    currentInstance,
    instanceTypeButtons,
    lastInstanceButton,
    currentDbOverviewInfo: currentViewInfo,
    getInstanceMultiMenuList,
    getInstanceSingleMenuList,
    getInstanceMultiViewClusterClusterView,
    getInstanceMenuListByDbType,
    getInstanceClusterView,
    getClusterViewMenuListByInstanceId,
    addInstanceIdToMenuPaths,
  };
};

// Dashboard
const useMenuDashboard = (
  props: Props,
  menuList: Ref<BreadCrumbMenuInfo | undefined>,
  isBreadCrumbModalLoading: Ref<boolean>,
) => {
  const { t } = useInternational();
  const route = useRoute();
  const dashboardListStore = useDashboardListStore();
  const { fetchDashboardList, updateFavoriteDashboardList } = dashboardListStore;
  const { dashboardList } = storeToRefs(dashboardListStore);
  const monitoringDashboard = computed(() => useDashboardViewStore().monitoringDashboard);

  const CURRENT_DASHBOARD_ROOT_MENU = {
    [BASE_MENU_VALUE.HOME]: t('WORD.GNB.HOME'),
    [BASE_MENU_VALUE.INFRASTRUCTURE]: t('WORD.INFRASTRUCTURE'),
    [BASE_MENU_VALUE.MESSAGE_QUEUE]: t('WORD.MESSAGE_QUEUE'),
    [BASE_MENU_VALUE.CLOUD]: t('WORD.CLOUD'),
    [BASE_MENU_VALUE.KUBERNETES]: t('WORD.KUBERNETES'),
    [BASE_MENU_VALUE.APPLICATION]: t('WORD.APPLICATION'),
    [BASE_MENU_VALUE.DATABASE]: t('WORD.DATABASE'),
    [BASE_MENU_VALUE.ORACLE]: t('WORD.ORACLE'),
    [BASE_MENU_VALUE.POSTGRESQL]: t('WORD.POSTGRESQL'),
    [BASE_MENU_VALUE.MYSQL]: t('WORD.MYSQL'),
    [BASE_MENU_VALUE.SQLSERVER]: t('WORD.SQL_SERVER'),
    [BASE_MENU_VALUE.REDIS]: t('WORD.REDIS'),
    [BASE_MENU_VALUE.CUBRID]: t('WORD.CUBRID'),
    [BASE_MENU_VALUE.MONGODB]: t('WORD.MONGODB'),
    [BASE_MENU_VALUE.TIBERO]: t('WORD.TIBERO'),
    [BASE_MENU_VALUE.ALTIBASE]: t('WORD.ALTIBASE'),
    [BASE_MENU_VALUE.CLICKHOUSE]: t('WORD.CLICKHOUSE'),
    [BASE_MENU_VALUE.REDSHIFT]: t('WORD.REDSHIFT'),
    [BASE_MENU_VALUE.SINGLESTORE]: t('WORD.SINGLESTORE'),
    [BASE_MENU_VALUE.BUSINESS]: t('WORD.BUSINESS'),
    [BASE_MENU_VALUE.LOGS]: t('WORD.LOGS'),
    [BASE_MENU_VALUE.ALERT]: t('WORD.ALERT'),
  } as const;

  const currentRootMenu = computed(() => {
    return CURRENT_DASHBOARD_ROOT_MENU[`${route.query.rootMenu}`];
  });

  const getDashboardMenuList = (): BreadCrumbMenuInfo => {
    const rootMenu =
      route.query.rootMenu === BASE_MENU_VALUE.KUBERNETES ? 'k8s' : route.query.rootMenu;
    return {
      children: dashboardList.value
        ?.filter((dashboard) => {
          if (route.query.menuType === DASHBOARD_MENU_TYPE.ANALYSIS_DASHBOARD) {
            return dashboard.analysisMenuPaths?.includes(`${rootMenu}`);
          }
          return dashboard.menuPaths?.includes(`${rootMenu}`);
        })
        ?.map((dashboard) => ({
          text: dashboard.dashboardName,
          value: `${dashboard.dashboardId}`,
          path: `/dashboard/view/${dashboard.dashboardId}?rootMenu=${route.query.rootMenu}&menuType=${route.query.menuType}`,
          favoriteId: dashboard.favoriteId,
        })),
    };
  };

  const changeFavorite = async (menu: BreadCrumbMenuInfo) => {
    if (!menu.value) {
      throw Error('menu value is not defined');
    }

    try {
      isBreadCrumbModalLoading.value = true;

      await updateFavoriteDashboardList([Number(menu.value)]);
      await fetchDashboardList();
      invalidatePlatformDashboardMenuPath();

      menuList.value = getDashboardMenuList();
    } catch (error) {
      console.log(error);
    } finally {
      isBreadCrumbModalLoading.value = false;
    }
  };
  provide(BREAD_CRUMBS_CHANGE_FAVORITE_KEY, changeFavorite); // breadCrumbElementLink.setup.ts에서만 사용

  watch(
    () => dashboardList.value,
    () => {
      if (props.mode === 'dashboard') {
        menuList.value = getDashboardMenuList();
      }
    },
  );

  return { monitoringDashboard, currentRootMenu, getDashboardMenuList };
};

export const setup = (props: Props) => {
  const { t } = useInternational();
  const menuList = ref<BreadCrumbMenuInfo>();
  const currentBreadCrumbId = ref<string>();
  const isShowModal = ref(false);
  const isBreadCrumbModalLoading = ref(false);
  const currentTarget = ref<HTMLButtonElement | null>(null);
  const menuPosition = ref<MenuPosition>({ top: '0', left: '0' });
  const activeModalMode = ref<BreadCrumbMode>(props.mode ?? 'common');

  const { filteredPaths, getSiblingMenuPath } = useMenuRoutes();
  const {
    instanceTitleByViewMode,
    viewDisplayName,
    currentInstance,
    instanceTypeButtons,
    lastInstanceButton,
    currentDbOverviewInfo,
    getInstanceSingleMenuList,
    getInstanceMenuListByDbType,
    getClusterViewMenuListByInstanceId,
    addInstanceIdToMenuPaths,
  } = useMenuInstances();
  const { monitoringDashboard, currentRootMenu, getDashboardMenuList } = useMenuDashboard(
    props,
    menuList,
    isBreadCrumbModalLoading,
  );

  const isMenuToggle = ref(false);
  const menuToggleIndex = ref<number | null | undefined>(null);

  const setMenuPosition = () => {
    const { top, left, height } = currentTarget.value!.getBoundingClientRect();
    menuPosition.value = {
      top: `${top + height}px`,
      left: `${left}px`,
    };
  };

  const initMenu = () => {
    currentBreadCrumbId.value = undefined;
    isShowModal.value = false;
    isMenuToggle.value = false;
    menuToggleIndex.value = null;
    menuList.value = undefined;
  };

  const setMenu = (
    target: HTMLButtonElement,
    i?: number,
    instanceType?: MenuMouseEvent['instanceType'],
    isCluster?: boolean,
  ) => {
    currentTarget.value = target;
    setMenuPosition();
    isShowModal.value = true;

    currentBreadCrumbId.value = target.dataset.breadCrumbId;
    if (props.mode === 'common') {
      activeModalMode.value = 'common';
      menuList.value = getSiblingMenuPath(i);
    } else if (props.mode === 'instance') {
      if (instanceType) {
        activeModalMode.value = 'instance';
        if (
          instanceType === OVERVIEW_TYPE.MULTI_VIEW ||
          instanceType === OVERVIEW_TYPE.CLUSTER_VIEW
        ) {
          if (isCluster) {
            menuList.value = getClusterViewMenuListByInstanceId();
          } else {
            menuList.value = getInstanceMenuListByDbType();
          }
        } else if (instanceType === OVERVIEW_TYPE.SINGLE_VIEW) {
          menuList.value = getInstanceSingleMenuList();
        }
      } else {
        activeModalMode.value = 'common';
        menuList.value = addInstanceIdToMenuPaths(getSiblingMenuPath(i));
      }
    } else if (props.mode === 'dashboard') {
      activeModalMode.value = 'dashboard';
      menuList.value = getDashboardMenuList();
    }
  };

  const menuMouseOver = ({ e, i, instanceType }: MenuMouseEvent, isCluster?: boolean) => {
    if (isMenuToggle.value) return;

    const target = e.currentTarget as HTMLButtonElement;
    setMenu(target, i, instanceType, isCluster);
  };

  const menuMouseClick = ({ e, i, instanceType }: MenuMouseEvent, isCluster?: boolean) => {
    if (menuToggleIndex.value === i) {
      initMenu();
    } else {
      const target = e.currentTarget as HTMLButtonElement;
      isMenuToggle.value = true;
      menuToggleIndex.value = i;
      setMenu(target, i, instanceType, isCluster);
    }
  };

  const menuMouseLeave = (e: MouseEvent) => {
    const target = e.relatedTarget;
    if (
      target instanceof HTMLElement &&
      (isMenuToggle.value || target?.closest(`.${BREAD_CRUMBS_MODAL_CLASS}`))
    ) {
      return;
    }
    initMenu();
  };

  const menuClose = () => {
    initMenu();
  };

  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const breadCrumbsArea = document.querySelector('.app-view-name');
    const modalArea = document.querySelector(`.${BREAD_CRUMBS_MODAL_CLASS}`);

    if (breadCrumbsArea && modalArea) {
      if (!breadCrumbsArea.contains(target) && !modalArea.contains(target)) {
        menuClose();
      }
    }
  };

  const resizeObserver = new ResizeObserver(() => {
    if (isShowModal.value) {
      setMenuPosition();
    }
  });

  const openSecondDepthMenu = () => {
    if (filteredPaths.value.length === 3) {
      nextTick(() => {
        const modalContent = document.querySelector('.route-paths-modal__content');
        const secondDepthMenu = filteredPaths.value[2];
        isMenuToggle.value = true;
        menuToggleIndex.value = 2;
        const target = document.querySelector(
          `[data-bread-crumb-id="${secondDepthMenu.value}"]`,
        ) as HTMLButtonElement;
        if (target && modalContent) {
          setMenu(target, 2);
          modalContent.scrollTop = 0;
        }
      });

      nextTick(() => {});
    }
  };

  watch(
    () => filteredPaths.value,
    (newPaths, oldPaths) => {
      if (
        newPaths.length === 3 &&
        (!oldPaths || newPaths[1] !== oldPaths[1]) &&
        isShowModal.value
      ) {
        openSecondDepthMenu();
      }
    },
    { deep: true },
  );

  onMounted(() => {
    const appHeader = document.querySelector('.app-header');

    if (appHeader) {
      resizeObserver.observe(appHeader);
    }

    document.addEventListener('click', handleOutsideClick);
  });

  onUnmounted(() => {
    resizeObserver.disconnect();
    document.removeEventListener('click', handleOutsideClick);
  });

  return {
    t,
    isShowModal,
    isBreadCrumbModalLoading,

    menuList,
    currentBreadCrumbId,
    filteredPaths,
    menuPosition,
    isMenuToggle,
    menuToggleIndex,

    instanceTitleByViewMode,
    viewDisplayName,
    currentInstance,
    instanceTypeButtons,
    lastInstanceButton,
    currentDbOverviewInfo,
    monitoringDashboard,
    currentRootMenu,

    activeModalMode,

    menuMouseOver,
    menuMouseClick,
    menuMouseLeave,
    menuClose,
    handleOutsideClick,
  };
};
